import React, { ChangeEvent, useContext, useState } from 'react';
import {
  Badge, Collapse, Form, FormGroup, Input, Label,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Typeahead } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import sortBy from 'lodash/sortBy';
import snakeCase from 'lodash/snakeCase';
import { OrderContext } from '../../../context/OrderContext';
import { CampaignContext } from '../../../context/CampaignWrapper';
import GooglePlacesComponent from '../../../components/GooglePlacesComponent';
import CustomFieldComponent from './CustomFieldComponent';
import CheckoutButton from './CheckoutButton';
import SellerGroup from '../../../utils/models/SellerGroup';
import AcceptPrivacyCheckbox from './AcceptPrivacyCheckbox';
import RequiredAsterisk from './RequiredAsterisk';
import { formatTimeslot, isDeliverMethod, omitArticleIds } from '../../../utils/helpers';
import { useLanguage } from '../../../utils/hooks/language.hook';
import InfoIcon from './InfoIcon';
import {
  DeliveryMethod, InputTimeslot, Timeslot, useSellerGroupsQuery,
} from '../../../generated/graphql';
import DonationForm from './DonationForm';

const CheckoutForm = () => {
  const {
    campaign: {
      slug, formFields, deliveryOptions, isSellerGroupRequired, timeslots, textConfig, articles,
    },
  } = useContext(CampaignContext);
  const { order, setPurchaserInfo, setOrder } = useContext(OrderContext);
  const {
    name: purchaserName, sellerGroupId, email, deliveryMethod: orderDeliveryMethod, address,
  } = order;
  const { data: sellerGroupsData } = useSellerGroupsQuery({ variables: { slug: slug || '' } });

  const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false);
  const [translate] = useTranslation();
  const locale = useLanguage();

  const { deliveryMethods: unorderedDeliveryMethods = [] } = deliveryOptions || {};
  const deliveryMethods = sortBy(unorderedDeliveryMethods);
  const deliveryMethod = orderDeliveryMethod || deliveryMethods[0];
  const hasDeliveryMethods = deliveryMethods?.length > 1;

  const donationArticle = articles?.find((a) => a.__typename === 'DonationArticle');
  const regularArticles = articles?.filter((a) => a.__typename === 'Article');
  const regularQuantity = Object.entries(order.quantities).reduce((total, [articleId, quantity]) => (
    regularArticles.some((a) => a._id === articleId) ? (total + quantity) : total
  ), 0);
  const isDonationOnly = !regularQuantity && !!donationArticle;

  const makeId = (ts: InputTimeslot) => `${ts.start}-${ts.end}-${ts.badge || ''}`;
  const options = sortBy(timeslots[deliveryMethod], (t) => new Date(t.start))
    .map((ts: InputTimeslot) => ({ id: makeId(ts), label: formatTimeslot(ts, locale), ...ts }));
  const defaultSelected = options.find((o) => order.collectTimeslot && o.id === makeId(order.collectTimeslot));
  const [selectedTimeslots, setSelectedTimeslots] = useState<Timeslot[]>(defaultSelected ? [defaultSelected] : []);

  const sellerGroups = (sellerGroupsData?.campaignBySlug?.sellerGroups || []).map((group) => new SellerGroup(group));
  const hasSellerGroups = !!sellerGroups.length;
  const parsedSellerGroups = sellerGroups.map((group) => ({ id: group._id, label: group.names, groupCode: group.code }));
  const autocompleteSelected = sellerGroups.find(({ _id }) => _id === sellerGroupId)?.names;

  const onSelectSellerGroup = (selected: Option) => {
    const value = (selected as Array<{ id: string }>)[0]?.id;

    setOrder((o) => ({
      ...o,
      sellerGroupId: value,
    }));
  };

  const acceptPrivacy = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setIsPrivacyAccepted(checked);
  };

  const deliveryOptionsRadio = deliveryMethods.map((method) => {
    let label = method && translate(`COMMON.DELIVERY_METHOD.${snakeCase(method).toUpperCase()}`);
    if (method === DeliveryMethod.Deliver && textConfig.deliveryLabel) label = textConfig.deliveryLabel;

    if (!method) return null;

    return (
      <div style={{ marginRight: 15 }} key={method}>
        <Input
          id={method}
          type="radio"
          name="deliveryMethod"
          onChange={(e) => {
            setSelectedTimeslots([]);

            const shippingCostArticle = articles.find((a) => a.__typename === 'ShippingCostsArticle');
            setOrder((o) => ({
              ...o,
              quantities: shippingCostArticle && e.target.value === DeliveryMethod.Deliver ? {
                ...o.quantities,
                [shippingCostArticle._id]: 1,
              } : omitArticleIds(o.quantities, [shippingCostArticle?._id]),
              collectTimeslot: undefined,
              deliveryMethod: e.target.value as DeliveryMethod,
            }));
          }}
          value={method}
          defaultChecked={deliveryMethod === method}
        />
        <Label for={method} className="mb-0 mx-2">
          {label}
        </Label>
      </div>
    );
  });

  const formFieldInputs = formFields.map((field) => (
    <CustomFieldComponent
      key={field._id}
      field={field}
    />
  ));

  const upperCaseDeliveryMethod = snakeCase(deliveryMethod || '').toUpperCase();

  const timeslotSelect = deliveryMethod && timeslots?.[deliveryMethod]?.length > 0 && !isDonationOnly && (
    <Typeahead
      paginationText={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.TIMESLOTS.SHOW_MORE_DATA')}
      maxResults={500}
      multiple={false}
      clearButton
      id="typeahead-collect-timeslots-list"
      options={options}
      labelKey={(option) => {
        if (typeof option === 'object' && option.label) {
          return `${option.label}${option.badge ? ` [${option.badge}]` : ''}`;
        }
        return '';
      }}
      renderMenu={(results, menuProps) => (
        <Menu {...menuProps}>
          {results.map((result, index) => {
            if (typeof result === 'object' && result.id && result.label) {
              const {
                id, label, isDisabled, badge,
              } = result;
              return (
                <MenuItem option={result} position={index} style={isDisabled ? { pointerEvents: 'none' } : {}} key={id}>
                  <span
                    className={`${isDisabled ? 'text-muted' : ''} text-wrap`}
                    style={isDisabled ? { textDecoration: 'line-through', opacity: 0.5 } : {}}
                  >
                    {label}
                    {badge && (
                      <>
                        {' '}
                        <Badge color="primary" className="text-wrap">{badge}</Badge>
                      </>
                    )}
                  </span>
                </MenuItem>
              );
            }
            return null;
          })}
        </Menu>
      )}
      placeholder={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.TIMESLOTS.TYPE_DATE')}
      emptyLabel={translate(`COMPONENTS.CHECKOUT_CONTAINER.FORM.TIMESLOTS.${upperCaseDeliveryMethod}.NOT_FOUND`)}
      onChange={(selected) => {
        if (typeof selected[0] === 'object' && (selected[0] || {}).start) {
          const { start, end, badge } = selected[0];
          setSelectedTimeslots([{
            start,
            end,
            ...selected[0],
          }]);
          setOrder((o) => ({
            ...o,
            collectTimeslot: { start, end, badge },
          }));
        } else {
          setSelectedTimeslots([]);
          setOrder((o) => ({
            ...o,
            collectTimeslot: undefined,
          }));
        }
      }}
      selected={selectedTimeslots}
      inputProps={{
        id: 'typeahead-collect-timeslots',
      }}
    />
  );

  return (
    <Form className="checkout-form my-3 my-md-4">
      <Collapse isOpen={hasSellerGroups}>
        <FormGroup>
          <Label for="typeahead-order-sellerGroup" className="mb-0">
            {textConfig.sellerGroupLabel || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.SUPPORT_VIA')}
            {isSellerGroupRequired && <RequiredAsterisk />}
            {!!textConfig.sellerGroupDescription && <InfoIcon text={textConfig.sellerGroupDescription} />}
          </Label>
          <Typeahead
            id="typeahead-order-sellerGroup"
            onChange={onSelectSellerGroup}
            defaultSelected={autocompleteSelected ? [autocompleteSelected] : undefined}
            options={parsedSellerGroups}
            maxResults={500}
            paginationText={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.SELLER_PAGINATION')}
            placeholder={textConfig.sellerGroupPlaceholder || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.SELLER_PLACEHOLDER')}
            emptyLabel={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.NO_SELLERS')}
            clearButton
          />
        </FormGroup>
      </Collapse>
      <FormGroup>
        <Label for="name-input" className="mb-0">
          {textConfig.nameLabel || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.NAME')}
          <RequiredAsterisk />
          {!!textConfig.nameDescription && <InfoIcon text={textConfig.nameDescription} />}
        </Label>
        <Input
          id="name-input"
          type="text"
          name="name"
          onChange={setPurchaserInfo}
          value={purchaserName}
          placeholder={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.NAME_PLACEHOLDER')}
        />
      </FormGroup>
      <FormGroup>
        <Label for="email-input" className="mb-0">
          {textConfig.emailLabel || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.EMAIL')}
          <RequiredAsterisk />
          {!!textConfig.emailDescription && <InfoIcon text={textConfig.emailDescription} />}
        </Label>
        <Input
          id="email-input"
          type="email"
          name="email"
          onChange={setPurchaserInfo}
          value={email}
          placeholder={translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.EMAIL_PLACEHOLDER')}
        />
      </FormGroup>
      <Collapse isOpen={hasDeliveryMethods && !isDonationOnly}>
        <FormGroup>
          <Label for="deliveryMethod" className="mb-0">
            {translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.DELIVERY')}
            <RequiredAsterisk />
          </Label>
          <div className="d-flex align-items-baseline">
            {deliveryOptionsRadio}
            {!!textConfig.delivery && <InfoIcon text={textConfig.delivery} />}
          </div>
        </FormGroup>
      </Collapse>
      <Collapse isOpen={isDeliverMethod(deliveryMethod) && !isDonationOnly}>
        <GooglePlacesComponent value={address} onChange={setPurchaserInfo} />
      </Collapse>
      <Collapse isOpen={!!timeslotSelect}>
        <FormGroup>
          <Label htmlFor="typeahead-collect-timeslots" className="mb-0">
            {translate(`COMPONENTS.CHECKOUT_CONTAINER.FORM.TIMESLOTS.${upperCaseDeliveryMethod}.DATE`)}
            <RequiredAsterisk />
          </Label>
          {timeslotSelect}
        </FormGroup>
      </Collapse>
      {formFieldInputs}
      {!!donationArticle && (
        <DonationForm
          donationArticleId={donationArticle._id}
          defaultValue={order.quantities[donationArticle._id]}
          setOrder={setOrder}
        />
      )}
      <AcceptPrivacyCheckbox isPrivacyAccepted={isPrivacyAccepted} acceptPrivacy={acceptPrivacy} />
      <FormGroup>
        <CheckoutButton isPrivacyAccepted={isPrivacyAccepted} />
      </FormGroup>
    </Form>
  );
};

export default CheckoutForm;
